import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { PageItem } from 'src/app/shared/interfaces/page-item.interface';
import { Sacpm, SacpmCreate } from 'src/app/shared/interfaces/sacpm/sacpm.interface';
import { SacpmOrigem } from 'src/app/shared/interfaces/sacpm/sacpm-origem.interface';
import { SacpmStatus } from 'src/app/shared/interfaces/sacpm/sacpm-status.interface';
import { SacpmNorma } from 'src/app/shared/interfaces/sacpm/sacpm-norma.interface';
import { SacpmOrigemReclamacaoClienteTipo } from 'src/app/shared/interfaces/sacpm/sacpm-origem-reclamacao-cliente-tipo.interface';
import { SacpmParticipante, SacpmParticipanteCreate } from 'src/app/shared/interfaces/sacpm/sacpm-participante.interface';
import { SacpmDetalhamento, SacpmDetalhamentoCreate } from 'src/app/shared/interfaces/sacpm/sacpm-detalhamento.interface';
import { SacpmAnaliseCausa, SacpmAnaliseCausaCreate } from 'src/app/shared/interfaces/sacpm/sacpm-analise-causa.interface';
import { SacpmPlanoAcao, SacpmPlanoAcaoCreate } from 'src/app/shared/interfaces/sacpm/sacpm-plano-acao.interface';
import { SacpmAnaliseEficacia, SacpmAnaliseEficaciaCreate } from 'src/app/shared/interfaces/sacpm/sacpm-analise-eficacia.interface';
import { SacpmKpi } from 'src/app/shared/interfaces/sacpm/sacpm-kpi.interface';
import { SacpmPlanoAcaoHistorico, SacpmPendenciaValidacao } from 'src/app/shared/interfaces/sacpm/sacpm-plano-acao-historico.interface';

@Injectable({
  providedIn: 'root'
})
export class SacpmAPIService {

  private baseUrl = `${environment.APIEndpoint}sacpm`;

  constructor(private http: HttpClient) {}

  // ── Tabelas de domínio ──────────────────────────────────────────
  listarOrigens(): Observable<SacpmOrigem[]> {
    return this.http.get<SacpmOrigem[]>(`${this.baseUrl}/origens`);
  }

  listarStatus(): Observable<SacpmStatus[]> {
    return this.http.get<SacpmStatus[]>(`${this.baseUrl}/status`);
  }

  listarNormas(): Observable<SacpmNorma[]> {
    return this.http.get<SacpmNorma[]>(`${this.baseUrl}/normas`);
  }

  listarOrigemReclamacaoClienteTipos(): Observable<SacpmOrigemReclamacaoClienteTipo[]> {
    return this.http.get<SacpmOrigemReclamacaoClienteTipo[]>(`${this.baseUrl}/origem-reclamacao-cliente-tipos`);
  }

  // ── SACPM principal ─────────────────────────────────────────────
  getAll(
    pageNumber: number = 1,
    pageSize: number = 11,
    numero: string = '',
    tipo: string = '',
    origemId: number | null = null,
    statusId: number | null = null,
    emitente: string = '',
    departamentoId: number | null = null,
    setorId: number | null = null,
    clienteId: number | null = null,
    dataInicial: string = '',
    dataFinal: string = '',
    normaId: number | null = null,
    origemReclamacaoClienteTipoId: number | null = null,
    reincidencia: boolean | null = null
  ): Observable<PageItem> {
    let params = `?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    if (numero)                          params += `&numero=${numero}`;
    if (tipo)                            params += `&tipo=${tipo}`;
    if (origemId)                        params += `&origemId=${origemId}`;
    if (statusId)                        params += `&statusId=${statusId}`;
    if (emitente)                        params += `&emitente=${emitente}`;
    if (departamentoId)                  params += `&departamentoId=${departamentoId}`;
    if (setorId)                         params += `&setorId=${setorId}`;
    if (clienteId)                       params += `&clienteId=${clienteId}`;
    if (dataInicial)                     params += `&dataInicial=${dataInicial}`;
    if (dataFinal)                       params += `&dataFinal=${dataFinal}`;
    if (normaId)                         params += `&normaId=${normaId}`;
    if (origemReclamacaoClienteTipoId)   params += `&origemReclamacaoClienteTipoId=${origemReclamacaoClienteTipoId}`;
    if (reincidencia !== null)           params += `&reincidencia=${reincidencia}`;
    return this.http.get<PageItem>(this.baseUrl + params);
  }

  getKpis(dataInicial: string, dataFinal: string): Observable<SacpmKpi> {
    const params = `?dataInicial=${dataInicial}&dataFinal=${dataFinal}`;
    return this.http.get<SacpmKpi>(`${this.baseUrl}/kpis${params}`);
  }

  getById(id: number): Observable<Sacpm> {
    return this.http.get<Sacpm>(`${this.baseUrl}/${id}`);
  }

  create(data: SacpmCreate): Observable<number> {
    return this.http.post<number>(this.baseUrl, data);
  }

  update(id: number, data: SacpmCreate): Observable<Sacpm> {
    return this.http.put<Sacpm>(`${this.baseUrl}/${id}`, data);
  }

  alterarStatus(id: number, statusId: number): Observable<boolean> {
    return this.http.put<boolean>(`${this.baseUrl}/${id}/status`, { statusId });
  }

  delete(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`);
  }

  // ── Participantes ───────────────────────────────────────────────
  getParticipantes(sacpmId: number): Observable<SacpmParticipante[]> {
    return this.http.get<SacpmParticipante[]>(`${this.baseUrl}/${sacpmId}/participantes`);
  }

  addParticipante(sacpmId: number, data: SacpmParticipanteCreate): Observable<number> {
    return this.http.post<number>(`${this.baseUrl}/${sacpmId}/participantes`, data);
  }

  removeParticipante(sacpmId: number, participanteId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${sacpmId}/participantes/${participanteId}`);
  }

  // ── Detalhamento ────────────────────────────────────────────────
  getDetalhamento(sacpmId: number): Observable<SacpmDetalhamento[]> {
    return this.http.get<SacpmDetalhamento[]>(`${this.baseUrl}/${sacpmId}/detalhamento`);
  }

  createDetalhamento(sacpmId: number, data: SacpmDetalhamentoCreate): Observable<number> {
    return this.http.post<number>(`${this.baseUrl}/${sacpmId}/detalhamento`, data);
  }

  updateDetalhamento(sacpmId: number, detId: number, data: SacpmDetalhamentoCreate): Observable<SacpmDetalhamento> {
    return this.http.put<SacpmDetalhamento>(`${this.baseUrl}/${sacpmId}/detalhamento/${detId}`, data);
  }

  // ── Análise de Causa ────────────────────────────────────────────
  getAnalisesCausa(sacpmId: number): Observable<SacpmAnaliseCausa[]> {
    return this.http.get<SacpmAnaliseCausa[]>(`${this.baseUrl}/${sacpmId}/analise-causa`);
  }

  addAnaliseCausa(sacpmId: number, data: SacpmAnaliseCausaCreate): Observable<number> {
    return this.http.post<number>(`${this.baseUrl}/${sacpmId}/analise-causa`, data);
  }

  updateAnaliseCausa(sacpmId: number, causaId: number, data: SacpmAnaliseCausaCreate): Observable<SacpmAnaliseCausa> {
    return this.http.put<SacpmAnaliseCausa>(`${this.baseUrl}/${sacpmId}/analise-causa/${causaId}`, data);
  }

  deleteAnaliseCausa(sacpmId: number, causaId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${sacpmId}/analise-causa/${causaId}`);
  }

  // ── Plano de Ação (5W2H) ────────────────────────────────────────
  listarPlanoAcaoStatus(): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/plano-acao-status`);
  }

  getPlanosAcao(sacpmId: number): Observable<SacpmPlanoAcao[]> {
    return this.http.get<SacpmPlanoAcao[]>(`${this.baseUrl}/${sacpmId}/plano-acao`);
  }

  addPlanoAcao(sacpmId: number, data: SacpmPlanoAcaoCreate): Observable<number> {
    return this.http.post<number>(`${this.baseUrl}/${sacpmId}/plano-acao`, data);
  }

  updatePlanoAcao(sacpmId: number, planoId: number, data: SacpmPlanoAcaoCreate): Observable<SacpmPlanoAcao> {
    return this.http.put<SacpmPlanoAcao>(`${this.baseUrl}/${sacpmId}/plano-acao/${planoId}`, data);
  }

  deletePlanoAcao(sacpmId: number, planoId: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${sacpmId}/plano-acao/${planoId}`);
  }

  // ── Plano de ação: validação da conclusão ───────────────────────
  // Concluir não é mais um status que o responsável grava direto: ele envia para validação
  // e o SGI aprova. Cada transição tem rota própria porque cada uma tem uma regra diferente
  // de quem pode — a API valida no WHERE do UPDATE, então 404 aqui pode significar
  // "não pode", e não só "não existe".

  enviarValidacaoPlanoAcao(sacpmId: number, planoId: number, observacao: string | null): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${sacpmId}/plano-acao/${planoId}/enviar-validacao`, { observacao });
  }

  aprovarPlanoAcao(sacpmId: number, planoId: number, observacao: string | null): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${sacpmId}/plano-acao/${planoId}/aprovar`, { observacao });
  }

  reprovarPlanoAcao(sacpmId: number, planoId: number, observacao: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${sacpmId}/plano-acao/${planoId}/reprovar`, { observacao });
  }

  reabrirPlanoAcao(sacpmId: number, planoId: number, observacao: string): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/${sacpmId}/plano-acao/${planoId}/reabrir`, { observacao });
  }

  getHistoricoPlanoAcao(sacpmId: number, planoId: number): Observable<SacpmPlanoAcaoHistorico[]> {
    return this.http.get<SacpmPlanoAcaoHistorico[]>(`${this.baseUrl}/${sacpmId}/plano-acao/${planoId}/historico`);
  }

  // Fila de validação do SGI. Não recebe usuário: a API resolve pelo token e devolve
  // lista vazia para quem não é do SGI.
  getPendenciasValidacao(): Observable<SacpmPendenciaValidacao[]> {
    return this.http.get<SacpmPendenciaValidacao[]>(`${this.baseUrl}/pendencias-validacao`);
  }

  // ── Exportação ──────────────────────────────────────────────────
  exportarExcel(
    numero: string = '',
    tipo: string = '',
    origemId: number | null = null,
    normaId: number | null = null,
    clienteId: number | null = null,
    dataInicial: string = '',
    dataFinal: string = '',
    statusId: number | null = null,
    origemReclamacaoClienteTipoId: number | null = null,
    reincidencia: boolean | null = null
  ): Observable<Blob> {
    let params = `?pageNumber=1&pageSize=10000`;
    if (numero)                          params += `&numero=${numero}`;
    if (tipo)                            params += `&tipo=${tipo}`;
    if (origemId)                        params += `&origemId=${origemId}`;
    if (normaId)                         params += `&normaId=${normaId}`;
    if (clienteId)                       params += `&clienteId=${clienteId}`;
    if (dataInicial)                     params += `&dataInicial=${dataInicial}`;
    if (dataFinal)                       params += `&dataFinal=${dataFinal}`;
    if (statusId)                        params += `&statusId=${statusId}`;
    if (origemReclamacaoClienteTipoId)   params += `&origemReclamacaoClienteTipoId=${origemReclamacaoClienteTipoId}`;
    if (reincidencia !== null)           params += `&reincidencia=${reincidencia}`;
    return this.http.get(`${this.baseUrl}/exportar-excel` + params, { responseType: 'blob' });
  }

  // ── Pendências do usuário ───────────────────────────────────────
  // Traz os planos onde o usuário é o responsável e que ainda dependem dele. Planos que
  // já foram enviados para validação não entram: a bola está com o SGI.
  obterMinhasPendencias(usuarioId: number): Observable<any[]> {
    return this.http.get<any[]>(`${this.baseUrl}/minhas-pendencias/${usuarioId}`);
  }

  // ── Análise de Eficácia ─────────────────────────────────────────
  getAnaliseEficacia(sacpmId: number): Observable<SacpmAnaliseEficacia[]> {
    return this.http.get<SacpmAnaliseEficacia[]>(`${this.baseUrl}/${sacpmId}/analise-eficacia`);
  }

  createAnaliseEficacia(sacpmId: number, data: SacpmAnaliseEficaciaCreate): Observable<number> {
    return this.http.post<number>(`${this.baseUrl}/${sacpmId}/analise-eficacia`, data);
  }

  updateAnaliseEficacia(sacpmId: number, eficaciaId: number, data: SacpmAnaliseEficaciaCreate): Observable<SacpmAnaliseEficacia> {
    return this.http.put<SacpmAnaliseEficacia>(`${this.baseUrl}/${sacpmId}/analise-eficacia/${eficaciaId}`, data);
  }
}
